import degit from 'degit';
import { execa } from 'execa';
import path from 'path';
import fs from 'fs-extra';

/**
 * Git下载器
 */
export class GitDownloader {
  /**
   * 解析Git地址
   */
  parseGitUrl(source: string, branch?: string): string {
    let url = source;

    // 处理简写格式
    if (source.startsWith('github:')) {
      url = source.replace('github:', '');
    } else if (source.startsWith('gitlab:')) {
      url = source.replace('gitlab:', 'gitlab:');
    } else if (source.startsWith('gitee:')) {
      url = source.replace('gitee:', 'gitee:');
    }

    // 添加分支
    if (branch && !url.includes('#')) {
      url = `${url}#${branch}`;
    }

    return url;
  }

  /**
   * 使用degit下载
   */
  async downloadWithDegit(
    source: string,
    destination: string,
    branch?: string
  ): Promise<void> {
    const url = this.parseGitUrl(source, branch);
    const emitter = degit(url, {
      cache: false,
      force: true,
      verbose: false,
    });

    await emitter.clone(destination);
  }

  /**
   * 使用git clone下载
   */
  async downloadWithClone(
    source: string,
    destination: string,
    branch?: string
  ): Promise<void> {
    let url = source;
    let extractedBranch = branch;

    // 如果URL中包含分支信息（如 url#branch），提取出来
    if (source.includes('#')) {
      const parts = source.split('#');
      url = parts[0];
      extractedBranch = parts[1];
    }

    // 转换简写格式为完整URL
    if (source.startsWith('github:')) {
      url = `https://github.com/${url.replace('github:', '')}`;
    } else if (source.startsWith('gitlab:')) {
      url = `https://gitlab.com/${url.replace('gitlab:', '')}`;
    } else if (source.startsWith('gitee:')) {
      url = `https://gitee.com/${url.replace('gitee:', '')}`;
    } else if (source.startsWith('codeup:')) {
      // 支持阿里云codeup简写: codeup:company/project/repo
      url = `https://codeup.aliyun.com/${url.replace('codeup:', '')}`;
    }

    // 确保URL以.git结尾（如果还没有）
    if (!url.endsWith('.git') && !url.includes('codeup.aliyun.com')) {
      url = `${url}.git`;
    }

    const args = ['clone', '--depth', '1'];
    if (extractedBranch) {
      args.push('-b', extractedBranch);
    }
    args.push(url, destination);

    await execa('git', args);

    // 删除.git目录
    const gitDir = path.join(destination, '.git');
    await fs.remove(gitDir);
  }

  /**
   * 判断是否为完整的HTTPS URL
   */
  isFullHttpsUrl(source: string): boolean {
    return source.startsWith('https://') || source.startsWith('http://');
  }

  /**
   * 判断是否为degit支持的简写格式
   */
  isDegitSupported(source: string): boolean {
    // degit支持: github:, gitlab:, bitbucket:, sourcehut:
    // 以及直接的 user/repo 格式（默认github）
    return (
      source.startsWith('github:') ||
      source.startsWith('gitlab:') ||
      source.startsWith('bitbucket:') ||
      source.startsWith('sourcehut:') ||
      (!source.includes('://') && !source.startsWith('gitee:') && !source.startsWith('codeup:'))
    );
  }

  /**
   * 下载模板（智能选择方式）
   */
  async download(source: string, destination: string): Promise<void> {
    // 如果是完整的HTTPS URL，使用git clone
    if (this.isFullHttpsUrl(source)) {
      try {
        await this.downloadWithClone(source, destination);
        return;
      } catch (error: any) {
        throw new Error(`Git clone 失败: ${error.message}`);
      }
    }

    // 如果是degit支持的格式，尝试使用degit
    if (this.isDegitSupported(source)) {
      try {
        await this.downloadWithDegit(source, destination);
        return;
      } catch (error: any) {
        // degit失败，尝试降级到git clone
        try {
          await this.downloadWithClone(source, destination);
          return;
        } catch (cloneError: any) {
          throw new Error(`下载失败: ${error.message}`);
        }
      }
    }

    // 其他格式（如gitee:, codeup:），使用git clone
    try {
      await this.downloadWithClone(source, destination);
    } catch (error: any) {
      throw new Error(`Git clone 失败: ${error.message}`);
    }
  }
}

export const gitDownloader = new GitDownloader();

